import React, { useState, useEffect } from 'react';
import type { Field } from './CustomTableConditionWithMultipleFields';
import Dropdown from '@/components/Dropdown/Dropdown';
import CustomTextInput from '../InputFields/CustomTextInput';
import CustomTextAreaInput from '../InputFields/CustomTextAreaInput';
import CustomToggle from '../InputFields/CustomToggel';
import Button from '@/components/Button/Button';

interface EditFieldPanelProps {
  mode: 'add' | 'edit';
  field?: Field;
  onSave: (field: Field) => void;
  onCancel: () => void;
}

const fieldTypeOptions = [
  { label: 'Text Input', value: 'text_input' },
  { label: 'Text Area', value: 'text_area' },
  { label: 'File Upload', value: 'file' },
  { label: 'No Action', value: 'no_action' },
];

const AddEditFieldPanel: React.FC<EditFieldPanelProps> = ({ mode, field, onSave, onCancel }) => {
  // Local state for all fields and switches
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState('');
  const [editable, setEditable] = useState(true);
  const [placeholder, setPlaceholder] = useState('');
  const [autoPopulate, setAutoPopulate] = useState(false);
  const [helpText, setHelpText] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [required, setRequired] = useState(true);
  const [error, setError] = useState<{
    label?: string;
    fieldType?: string;
    dataIdentifier?: string;
  }>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && field) {
      setLabel(field.label);
      setFieldType(field.fieldType || field.field_type || '');
      setEditable(field.additional_config?.editable ?? true);
      setPlaceholder(field.additional_config?.placeholder || '');
      setAutoPopulate(field.additional_config?.autoPopulate ?? false);
      setHelpText(field.additional_config?.helpText || '');
      setDefaultValue(field.additional_config?.defaultValue || '');
      setRequired(field.required ?? true);
    } else {
      setLabel('');
      setFieldType('');
      setEditable(true);
      setPlaceholder('');
      setAutoPopulate(false);
      setHelpText('');
      setDefaultValue('');
      setRequired(true);
    }
    setError({});
    setHasSubmitted(false);
  }, [mode, field]);

  const handleClearAll = () => {
    setLabel('');
    setFieldType('');
    setEditable(true);
    setPlaceholder('');
    setAutoPopulate(false);
    setHelpText('');
    setDefaultValue('');
    setRequired(true);
    setError({});
  };

  const handleUpdate = () => {
    setHasSubmitted(true);
    const newError: { label?: string; fieldType?: string; dataIdentifier?: string } = {};
    if (!label.trim()) newError.label = 'Field name is required.';
    if (!fieldType) newError.fieldType = 'Field type is required.';
    setError(newError);
    if (Object.keys(newError).length > 0) return;
    const fieldData: any = {
      label,
      fieldType,
      field_type: fieldType,
      required,
      additional_config: {
        editable,
        placeholder,
        defaultValue,
        autoPopulate,
        helpText,
      },
    };

    // Only include id for existing fields (edit mode)
    if (field?.id) {
      fieldData.id = field.id;
    }

    onSave(fieldData);
  };

  return (
    <div className="flex flex-col min-h-[500px] h-full">
      {/* Panels Row */}
      <div className="flex flex-1 relative">
        {/* Left Side */}
        <div className="w-1/2 pr-6 flex flex-col gap-6 ">
          <div>
            <label className="text-xs font-semibold text-ds-gray-500">
              Action Field Name <span className="text-ds-red-400">*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="rounded px-3 py-2 mt-1 text-ds-gray-500 font-semibold text-xs w-full  outline-none placeholder:text-ds-gray-300
            placeholder:text-xs border-2 border-ds-gray-100"
              placeholder="Enter relevant field name"
            />
            {hasSubmitted && error.label && (
              <div className="text-xs text-red-500 mt-1">{error.label}</div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-ds-gray-500">
              Field Type <span className="text-ds-red-400">*</span>
            </label>
            <Dropdown
              options={fieldTypeOptions}
              selectedValue={fieldType}
              onChange={setFieldType}
              error={hasSubmitted && !!error.fieldType}
              errorMessage={hasSubmitted ? error.fieldType : undefined}
              isFullWidth
            />
          </div>

          {(fieldType === 'text_input' || fieldType === 'text_area') && (
            <>
              <div>
                <CustomToggle
                  label="Editable Field"
                  description="Allow users to modify the value. If disabled, the field will be read-only."
                  checked={editable}
                  onChange={setEditable}
                  className="pr-3"
                />
              </div>
              <div>
                <CustomTextInput
                  label="Placeholder Value"
                  name="placeholder"
                  value={placeholder}
                  onChange={(e) => setPlaceholder(e.target.value)}
                  placeholder="Please provide placeholder value"
                  description="Provide a placeholder value for user to identify field and provide relevant entry."
                  className="text-ds-gray-500 font-semibold text-xs w-full"
                />
              </div>
            </>
          )}

          {fieldType === 'file' && (
            <div>
              <CustomToggle
                label="Mandatory Field"
                description="Enable this if the person capturing the data should not miss this."
                checked={required}
                onChange={setRequired}
                className="pr-3"
              />
            </div>
          )}
        </div>

        {/* Vertical Divider */}
        <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px bg-gray-200" />

        {/* Right Side */}
        <div className="w-1/2 flex flex-col gap-6 p-6">
          {(fieldType === 'text_input' || fieldType === 'text_area') && (
            <>
              {/* Help Text */}
              <div className="w-full">
                <CustomTextAreaInput
                  label="Help Text"
                  name="helpText"
                  value={helpText}
                  onChange={(e) => setHelpText(e.target.value)}
                  placeholder="This will help in guiding the person filling this field."
                  rows={4}
                  textAreaClassname="w-full"
                />
              </div>
              <div>
                <CustomTextInput
                  label="Default Value"
                  name="defaultValue"
                  value={defaultValue}
                  onChange={(e) => setDefaultValue(e.target.value)}
                  placeholder="Default value"
                  className="text-ds-gray-500 font-semibold text-xs "
                />
              </div>
            </>
          )}
          {fieldType === 'file' && (
            <div>
              <CustomTextAreaInput
                label="Help Text"
                name="helpText"
                value={helpText}
                onChange={(e) => setHelpText(e.target.value)}
                placeholder="This will help in guiding the person filling this field."
                rows={4}
                textAreaClassname="w-full"
              />
            </div>
          )}
        </div>
      </div>
      {/* Bottom Buttons */}
      <div className="w-full flex justify-between p-4 bg-white">
        <button className="border px-4 py-2 rounded text-xs" onClick={handleClearAll}>
          Clear All
        </button>
        <div className="flex gap-2">
          <button className="border px-4 py-2 rounded text-xs" onClick={onCancel}>
            Cancel
          </button>
          <Button onClick={handleUpdate} color="primary" className="text-xs w-full">
            {mode === 'edit' ? 'Update' : 'Add Action'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddEditFieldPanel;
